<template>
  <div class="rich-text-content" v-html="processedContent" ref="contentRef"></div>
  
  <!-- 图片预览器 -->
  <el-image-viewer
    v-if="showPreview"
    :url-list="allImages"
    :initial-index="currentPreviewIndex"
    @close="closePreview"
    teleported
    :show-progress="true"
    class="rich-text-image-viewer"
  />
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { ElImageViewer } from 'element-plus'

interface Props {
  content: string
  enableImagePreview?: boolean
  maxImageWidth?: string
  imageClass?: string
  enableIndent?: boolean // 新增：是否启用缩进
}

const props = withDefaults(defineProps<Props>(), {
  enableImagePreview: true,
  maxImageWidth: '100%',
  imageClass: 'rich-text-image',
  enableIndent: true // 默认启用缩进
})

// DOM引用
const contentRef = ref<HTMLElement>()

// 预览相关状态
const showPreview = ref(false)
const currentPreviewIndex = ref(0)

// 所有从文本中提取的图片URL
const allImages = ref<string[]>([])

// 处理后的内容
const processedContent = computed(() => {
  let result = props.content
  
  // 1. 清理文本
  result = cleanText(result)
    
  // 2. 处理图片（自动提取所有图片）
  result = processImages(result)
  
  // 3. 处理链接
  result = processLinks(result)
  
  // 4. 处理段落缩进
  result = processParagraphs(result)
  
  return result
})

// 清理文本
function cleanText(text: string): string {
  return text
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
}

// 处理图片并提取所有图片URL
function processImages(text: string): string {
  // 清空之前的图片数组
  allImages.value = []
  
  // 用于收集图片URL的临时数组
  const tempImages: string[] = []
  
  // 处理图片标签和图片URL
  let processedText = text
  
  // 处理已存在的img标签
  processedText = processedText.replace(
    /<img[^>]*src\s*=\s*["']([^"']+)["'][^>]*>/gi,
    (match, src) => {
      const imageUrl = cleanUrl(src)
      if (imageUrl && !tempImages.includes(imageUrl)) {
        tempImages.push(imageUrl)
      }
      
      const imageIndex = tempImages.indexOf(imageUrl)
      const cursorStyle = props.enableImagePreview ? 'cursor: pointer;' : ''
      const maxWidthStyle = props.maxImageWidth ? `max-width: ${props.maxImageWidth};` : ''
      
      // 保留原有属性，添加预览功能
      return match.replace(
        /<img([^>]*)>/i,
        (imgTag, attributes) => {
          const cleanAttributes = attributes.replace(/\s+onclick\s*=\s*["'][^"']*["']/gi, '')
          // 添加居中容器包裹图片
          return `
            <div class="rich-text-image-container">
              <img${cleanAttributes} 
                data-image-index="${imageIndex}"
                class="${props.imageClass}"
                style="${maxWidthStyle} ${cursorStyle}"
                loading="lazy">
            </div>
          `
        }
      )
    }
  )
  
  // 处理纯图片URL（没有被img标签包裹的）
  processedText = processedText.replace(
    /(https?:\/\/[^\s<>"']+?\.(?:jpe?g|png|gif|bmp|webp|svg)(?:\?[^\s<>"']*)?)/gi,
    (match) => {
      const imageUrl = cleanUrl(match)
      tempImages.push(imageUrl)
      
      const imageIndex = tempImages.indexOf(imageUrl)
      const cursorStyle = props.enableImagePreview ? 'cursor: pointer;' : ''
      const maxWidthStyle = props.maxImageWidth ? `max-width: ${props.maxImageWidth};` : ''
      
      // 转换为img标签并添加居中容器
      return `
        <div class="rich-text-image-container">
          <img src="${imageUrl}" 
            alt="图片" 
            data-image-index="${imageIndex}"
            class="${props.imageClass}"
            style="${maxWidthStyle} ${cursorStyle}"
            loading="lazy">
        </div>
      `
    }
  )
  
  // 更新所有图片数组
  allImages.value = [...tempImages]
  
  return processedText
}

// 清理URL
function cleanUrl(url: string): string {
  let cleaned = url
    .replace(/^["']+/, '')
    .replace(/["']+$/, '')
    .replace(/[.,;:!?]+$/, '')
  
  try {
    if (cleaned.includes('%')) {
      cleaned = decodeURIComponent(cleaned)
    }
    cleaned = encodeURI(cleaned)
  } catch (e) {
    console.warn('URL编码失败:', cleaned, e)
  }
  return cleaned
}

// 处理链接（跳过图片链接）
function processLinks(text: string): string {
  // 先移除图片部分，避免重复处理
  const imgContainerRegex = /<div class="rich-text-image-container">.*?<\/div>/gi
  const parts = text.split(imgContainerRegex)
  const imgContainers = text.match(imgContainerRegex) || []
  
  const processedParts = parts.map(part => {
    return part.replace(
      /(https?:\/\/[^\s<>"']+)/gi,
      (url) => {
        // 跳过图片链接（应该已经被处理为img标签）
        if (/\.(?:jpe?g|png|gif|bmp|webp|svg)(?:\?[^\s<>"']*)?$/i.test(url)) {
          return url
        }
        return `<a href="${encodeURI(url)}" target="_blank" rel="noopener noreferrer" class="rich-text-link">${url}</a>`
      }
    )
  })
  
  // 重新组合
  let result = ''
  for (let i = 0; i < processedParts.length; i++) {
    result += processedParts[i]
    if (imgContainers[i]) {
      result += imgContainers[i]
    }
  }
  
  return result
}

// 处理段落缩进
function processParagraphs(text: string): string {
  if (!props.enableIndent) {
    return text
  }
  
  // 先提取所有图片容器，避免它们被换行符处理影响
  const imageContainerRegex = /<div class="rich-text-image-container">.*?<\/div>/gs
  const imageContainers = []
  let processedText = text
  
  // 替换图片容器为占位符
  processedText = processedText.replace(imageContainerRegex, (match) => {
    const placeholder = `__IMAGE_CONTAINER_${imageContainers.length}__`
    imageContainers.push(match)
    return placeholder
  })
  
  // 按一个或多个换行符分割文本，过滤掉空行
  const lines = processedText.split(/\n+/).filter(line => line.trim() !== '')
  
  // 将占位符替换回图片容器
  const processedLines = lines.map(line => {
    return line.replace(/__IMAGE_CONTAINER_(\d+)__/g, (match, index) => {
      return imageContainers[parseInt(index)] || ''
    })
  })
  
  // 用<p>标签包裹非空行，并保留图片容器的独立性
  return processedLines
    .map(line => {
      // 如果是图片容器，则直接返回（不包裹在p标签内）
      if (line.includes('rich-text-image-container')) {
        return line
      }
      // 否则用p标签包裹
      return `<p class="rich-text-paragraph">${line}</p>`
    })
    .join('')
}

// 打开图片预览
function openImagePreview(index: number) {
  if (!props.enableImagePreview || allImages.value.length === 0) {
    return
  }
  
  // 确保索引在有效范围内
  const safeIndex = Math.max(0, Math.min(index, allImages.value.length - 1))
  currentPreviewIndex.value = safeIndex
  showPreview.value = true
}

// 关闭预览
function closePreview() {
  showPreview.value = false
}

// 处理图片点击事件
function handleImageClick(event: Event) {
  const target = event.target as HTMLElement
  if (target.tagName === 'IMG' && target.classList.contains(props.imageClass)) {
    event.preventDefault()
    event.stopPropagation()
    
    const index = target.getAttribute('data-image-index')
    if (index !== null) {
      openImagePreview(parseInt(index, 10))
    }
  }
}

// 初始化事件监听
function initEventListeners() {
  if (contentRef.value) {
    contentRef.value.addEventListener('click', handleImageClick)
  }
}

// 清理事件监听
function cleanupEventListeners() {
  if (contentRef.value) {
    contentRef.value.removeEventListener('click', handleImageClick)
  }
}

onMounted(() => {
  nextTick(() => {
    initEventListeners()
  })
})

onUnmounted(() => {
  cleanupEventListeners()
})
</script>

<style lang="scss" scoped>
.rich-text-content {
  line-height: 1.6;
  font-size: 14px;
  color: #333;
  word-break: break-word;
  overflow-wrap: break-word;
  
  // 段落缩进样式
  :deep(.rich-text-paragraph) {
    text-indent: 2em; // 首行缩进2个字符
    margin: 0.5em 0; // 段落间距
    line-height: 1.8;
    
    // 清除第一个段落的首行缩进（可选，根据需求）
    &:first-child {
      margin-top: 0;
    }
  }
  
  // 确保b和strong标签都有加粗样式
  :deep(b) {
    font-weight: bold;
  }
  
  :deep(strong) {
    font-weight: bold;
  }
  
  :deep(b *) {
    font-weight: bold !important;
  }
  
  :deep(strong *) {
    font-weight: bold !important;
  }
  
  // 链接样式
  :deep(.rich-text-link) {
    color: #1890ff;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  // 图片容器 - 实现居中
  :deep(.rich-text-image-container) {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 16px 0;
    text-align: center;
    clear: both;
    background-color: white;
    
    // 调整图片周围的间距
    br + & {
      margin-top: 20px;
    }
    
    & + br {
      display: none;
    }
  }
  
  // 图片样式
  :deep(.rich-text-image) {
    max-width: 100% !important;
    height: auto !important;
    border-radius: 4px;
    transition: opacity 0.2s ease;
    display: inline-block; // 改为inline-block以便在容器中居中
    background-color: white;
    &:hover {
      opacity: 0.9;
    }
  }
  
  // 确保所有图片都不会浮动
  :deep(img) {
    float: none !important;
  }
  // 预览器图片的白色背景（使用深度选择器）
  :deep(.rich-text-image-viewer) {
    .el-image-viewer__canvas {
      img {
        background-color: white; // 预览图片添加白色背景
      }
    }
  }
}
</style>